import {useCallback, useEffect, useMemo, useRef, useState} from 'react'
import type {PendingMessage} from '@microsoft/agent-host-protocol'
import {IconButton, VisuallyHidden} from '@primer/react'
import {ArrowUpIcon, TrashIcon} from '@primer/octicons-react'
import {announce} from '@github-ui/aria-live'
import {clsx} from 'clsx'
import {UserMessageBubble} from '../UserMessageBubble'
import {extractUserMessageImages} from './user-message-images'
import {extractChipAttachments} from './AhpTurnView'
import turnStyles from '../EventSessionLogs.module.css'
import messageStyles from './AhpTurnView.module.css'
import styles from './AhpPendingMessage.module.css'
import {useReadOnlyAhpHistory} from './AhpSessionContext'
import {useSessionContext} from '../../../contexts/SessionContext'
import {getChannelOrigin} from '../../../types/origin'
import {useOptionalEstablishedAhpSession} from '@github-ui/copilot-shared-ui/hooks/use-established-ahp-session'
import {
  clientInteractionAccepted,
  clientInteractionFailed,
} from '@github-ui/copilot-browser-latency/copilot-client-interaction'
import {useQueuedClientInteractions} from '@github-ui/copilot-browser-latency/use-copilot-client-interactions'

interface AhpPendingMessageProps {
  message: PendingMessage
  /** When provided, renders a control to cancel the queued message. */
  onCancel?: (id: string) => void | Promise<void>
  /** When provided, renders a control to send the queued message immediately. */
  onSendNow?: (message: PendingMessage) => void | Promise<void>
}

const ACTION_LABEL_MAX = 80

function actionLabel(action: 'Remove' | 'Send now', text: string | undefined): string {
  const trimmed = text?.trim()
  if (!trimmed) return `${action} queued message`
  const preview = trimmed.length > ACTION_LABEL_MAX ? `${trimmed.slice(0, ACTION_LABEL_MAX)}…` : trimmed
  return `${action} queued message: ${preview}`
}

/**
 * Queue actions are confirmed asynchronously by the relay echo, so the focused
 * control unmounts on a later render. Find a stable neighboring target before
 * starting the action so focus can move there after it succeeds.
 */
interface QueuedActionFocusTargets {
  siblingAction?: HTMLElement
  lastTurn?: HTMLElement
  timeline?: HTMLElement
}

function queuedActionFocusTargets(button: HTMLElement): QueuedActionFocusTargets {
  const timeline = button.closest<HTMLElement>('[data-testid="ahp-session-timeline"]')
  if (!timeline) return {}

  const actionGroups = Array.from(timeline.querySelectorAll<HTMLElement>('[data-queued-actions]'))
  const currentGroup = button.closest<HTMLElement>('[data-queued-actions]')
  const index = currentGroup ? actionGroups.indexOf(currentGroup) : -1
  const siblingGroup = actionGroups[index + 1] ?? actionGroups[index - 1]
  const siblingAction = siblingGroup?.querySelector<HTMLElement>('button')
  const turns = timeline.querySelectorAll<HTMLElement>('[data-turn-id]')
  return {
    ...(siblingAction ? {siblingAction} : {}),
    ...(turns[turns.length - 1] ? {lastTurn: turns[turns.length - 1]} : {}),
    timeline,
  }
}

/**
 * A user message queued to run as its own turn once the current turn finishes.
 * Rendered like a real user message but muted, with optional queue actions.
 */
export function AhpPendingMessage({message, onCancel, onSendNow}: AhpPendingMessageProps) {
  const {session} = useSessionContext()
  const readOnlyHistory = useReadOnlyAhpHistory()
  const establishedSession = useOptionalEstablishedAhpSession()
  const observeSendNow = useQueuedClientInteractions({
    role: readOnlyHistory ? 'read_only' : 'writable',
    session_id: establishedSession?.identity?.sessionId,
    environment_id: establishedSession?.identity?.environmentId,
    task_id: establishedSession?.identity?.taskId,
    chat_id: establishedSession?.identity?.channel?.startsWith('ahp-chat:')
      ? establishedSession.identity.channel
      : undefined,
  })
  const rowRef = useRef<HTMLLIElement>(null)
  const userText = message.message?.text
  const userImages = useMemo(() => extractUserMessageImages(message.message), [message.message])
  const chipAttachments = useMemo(() => extractChipAttachments(message.message), [message.message])
  const [pendingAction, setPendingAction] = useState<'remove' | 'send'>()
  const [error, setError] = useState<string>()
  const actionSucceededRef = useRef(false)
  const focusTargetsRef = useRef<QueuedActionFocusTargets>({})
  const preserveFocusWhenActionDisappears = useCallback(
    (button: HTMLButtonElement) => () => {
      if (document.activeElement !== button) return
      queueMicrotask(() => rowRef.current?.focus())
    },
    [],
  )

  useEffect(
    () => () => {
      if (!actionSucceededRef.current) return
      const {siblingAction, lastTurn, timeline} = focusTargetsRef.current
      queueMicrotask(() => {
        if (siblingAction?.isConnected) {
          siblingAction.focus()
        } else if (lastTurn?.isConnected) {
          lastTurn.focus()
        } else if (timeline?.isConnected) {
          // Every turn can be suppressed, so the timeline is the last target that is always present.
          timeline.focus()
        }
      })
    },
    [],
  )

  async function runAction(action: 'remove' | 'send', button: HTMLButtonElement, event: Event) {
    if (pendingAction) return
    const interaction = action === 'send' ? observeSendNow?.(message, event, button) : undefined
    const callback = action === 'remove' ? () => onCancel?.(message.id) : () => onSendNow?.(message)
    focusTargetsRef.current = queuedActionFocusTargets(button)
    actionSucceededRef.current = false
    setPendingAction(action)
    setError(undefined)
    try {
      await callback()
      clientInteractionAccepted(interaction)
      actionSucceededRef.current = true
      announce(action === 'remove' ? 'Queued message removed.' : 'Queued message sent now.')
    } catch {
      clientInteractionFailed(interaction)
      const errorMessage =
        action === 'remove' ? 'Queued message could not be removed.' : 'Queued message could not be sent now.'
      setError(errorMessage)
      announce(errorMessage, {assertive: true})
      setPendingAction(undefined)
    }
  }

  return (
    <li ref={rowRef} tabIndex={-1} className={clsx(turnStyles.turn, styles.row)}>
      {readOnlyHistory ? (
        <p>Queued in saved history.</p>
      ) : (
        <VisuallyHidden>Queued message waiting for agent</VisuallyHidden>
      )}
      <UserMessageBubble
        collapsible={false}
        showAvatar={!!getChannelOrigin(session.event_type)}
        content={userText}
        images={userImages}
        attachments={chipAttachments}
        bubbleClassName={clsx(messageStyles.pendingSteeringBubble, styles.bubble)}
        footer={
          <div className={styles.footer}>
            {!readOnlyHistory && (
              <div className={styles.status} aria-hidden="true">
                Queued
              </div>
            )}
            {!readOnlyHistory && (onCancel || onSendNow) ? (
              <div className={styles.actions} data-queued-actions="" data-testid="queued-message-actions">
                {onCancel ? (
                  <IconButton
                    ref={preserveFocusWhenActionDisappears}
                    type="button"
                    icon={TrashIcon}
                    variant="invisible"
                    size="small"
                    aria-label={actionLabel('Remove', userText)}
                    description={pendingAction === 'remove' ? undefined : 'Remove from queue'}
                    loading={pendingAction === 'remove'}
                    loadingAnnouncement="Removing queued message"
                    inactive={Boolean(pendingAction)}
                    onClick={event => void runAction('remove', event.currentTarget, event.nativeEvent)}
                  />
                ) : null}
                {onSendNow ? (
                  <IconButton
                    ref={preserveFocusWhenActionDisappears}
                    type="button"
                    icon={ArrowUpIcon}
                    variant="default"
                    size="small"
                    className={styles.sendNowButton}
                    aria-label={actionLabel('Send now', userText)}
                    description={pendingAction === 'send' ? undefined : 'Send now'}
                    loading={pendingAction === 'send'}
                    loadingAnnouncement="Sending queued message now"
                    inactive={Boolean(pendingAction)}
                    onClick={event => void runAction('send', event.currentTarget, event.nativeEvent)}
                  />
                ) : null}
              </div>
            ) : null}
            {error ? <div className={styles.error}>{error}</div> : null}
          </div>
        }
      />
    </li>
  )
}
